import React, { useState, useEffect, useRef } from "react";
import API from "../../config/api";
import { useAuth } from "../../hooks/useAuth";

const Spinner = ({ color = "#fff" }) => (
  <>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    <span style={{ display: "inline-block", width: 12, height: 12, border: `2px solid ${color}33`, borderTop: `2px solid ${color}`, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
  </>
);

export default function KnowledgeBasePage() {
  const { getToken } = useAuth();
  const [sources, setSources] = useState([]);
  const [tab, setTab] = useState("url"); // url | file | text
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asyncPending, setAsyncPending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const fileRef = useRef();

  const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` });

  useEffect(() => { loadSources(); }, []); // eslint-disable-line

  const loadSources = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const res = await fetch(API.kbList, { headers: authHeaders() });
      const data = await res.json();
      setSources(data.sources || []);
    } catch (e) { console.error(e); }
    finally { if (showSpinner) setRefreshing(false); }
  };

  const train = async () => {
    setLoading(true); setMsg({ text: "", type: "" });
    try {
      let body = {};
      if (tab === "url") {
        if (!url.trim()) throw new Error("Enter a URL");
        body = { type: "url", url: url.trim() };
      } else if (tab === "text") {
        if (!text.trim()) throw new Error("Enter text content");
        body = { type: "text", text: text.trim(), name: "Manual text" };
      } else if (tab === "file") {
        if (!file) throw new Error("Select a file");
        const fileType = file.name.endsWith(".pdf") ? "pdf" : "docx";
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        body = { type: fileType, file_data: fileData, name: file.name };
      }

      const res = await fetch(API.kbTrain, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Training failed");
      // 202 = async training started (URL crawl running in background)
      if (res.status === 202 || data.async) {
        setMsg({ text: `${data.message}`, type: "info" });
        setAsyncPending(true);
        setCountdown(120);
        const startCount = sources.length;
        const interval = setInterval(async () => {
          setCountdown(c => {
            if (c <= 1) {
              clearInterval(interval);
              setAsyncPending(false);
              setMsg({ text: "⏱ Training timed out. Please refresh manually.", type: "error" });
              loadSources();
              return 0;
            }
            return c - 1;
          });
          // Poll every 10 seconds to check if training completed
          try {
            const pollRes = await fetch(API.kbList, { headers: authHeaders() });
            const pollData = await pollRes.json();
            if ((pollData.sources || []).length > startCount) {
              clearInterval(interval);
              setSources(pollData.sources);
              setAsyncPending(false);
              setCountdown(0);
              setMsg({ text: "✅ Training completed successfully!", type: "success" });
            }
          } catch (e) { /* ignore poll errors */ }
        }, 10000);
      } else {
        setMsg({ text: `✅ ${data.message}`, type: "success" });
        setTimeout(() => loadSources(), 500);
      }
      setUrl(""); setText(""); setFile(null);
    } catch (e) { setMsg({ text: `❌ ${e.message}`, type: "error" }); }
    finally { setLoading(false); }
  };

  const deleteSource = async (source_id) => {
    if (!window.confirm("Delete this knowledge source?")) return;
    try {
      await fetch(API.kbDelete, {
        method: "DELETE", headers: authHeaders(), body: JSON.stringify({ source_id }),
      });
      loadSources();
    } catch (e) { console.error(e); }
  };

  return (
    <div style={S.page}>
      <h2 style={S.title}>📚 Knowledge Base</h2>
      <p style={S.sub}>Train your AI with your company's knowledge. The AI will use this during calls.</p>

      {/* Source type tabs */}
      <div style={S.tabs}>
        {[["url", "🌐 Website URL"], ["file", "📄 PDF / Word"], ["text", "✏️ Plain Text"]].map(([key, label]) => (
          <button key={key} style={{ ...S.tab, ...(tab === key ? S.tabActive : {}) }} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      <div style={S.trainBox}>
        {tab === "url" && (
          <input style={S.input} placeholder="https://yourcompany.com/about"
            value={url} onChange={e => setUrl(e.target.value)} />
        )}
        {tab === "file" && (
          <div>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc" style={{ display: "none" }}
              onChange={e => setFile(e.target.files[0])} />
            <button style={S.fileBtn} onClick={() => fileRef.current.click()}>
              {file ? `📎 ${file.name}` : "Choose PDF or Word file"}
            </button>
          </div>
        )}
        {tab === "text" && (
          <textarea style={S.textarea} placeholder="Paste your company description, product info, FAQs..."
            value={text} onChange={e => setText(e.target.value)} rows={6} />
        )}
        <button style={{ ...S.trainBtn, ...(loading || asyncPending ? S.trainBtnDisabled : {}) }} onClick={train} disabled={loading || asyncPending}>
          {loading ? <><Spinner /> Training...</> : "Train Knowledge Base"}
        </button>
        {msg.text && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            {asyncPending && <Spinner color="#58a6ff" />}
            <p style={{ color: msg.type === "success" ? "#3fb950" : msg.type === "info" ? "#58a6ff" : "#f85149", margin: 0, fontSize: 13 }}>
              {msg.text}{asyncPending && countdown > 0 ? ` (checking in ${countdown}s…)` : ""}
            </p>
          </div>
        )}
      </div>

      {/* Existing sources */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 28, marginBottom: 12 }}>
        <h3 style={{ color: "#e6edf3", margin: 0 }}>Trained Sources ({sources.length})</h3>
        <button style={S.refreshBtn} onClick={() => loadSources(true)} disabled={refreshing} title="Refresh sources">
          {refreshing ? <Spinner color="#8b949e" /> : "↻"}
        </button>
      </div>
      {sources.length === 0 ? (
        <p style={{ color: "#8b949e", fontSize: 13 }}>No sources trained yet. Add your first source above.</p>
      ) : (
        sources.map(s => (
          <div key={s.source_id} style={S.sourceRow}>
            <div>
              <span style={S.sourceType}>{s.source_type}</span>
              <span style={S.sourceName}>{s.source_name}</span>
              <span style={S.sourceChunks}>{s.chunks} chunks</span>
            </div>
            <button style={S.deleteBtn} onClick={() => deleteSource(s.source_id)}>🗑</button>
          </div>
        ))
      )}
    </div>
  );
}

const S = {
  page: { padding: "24px 0" },
  title: { color: "#e6edf3", marginBottom: 6 },
  sub: { color: "#8b949e", fontSize: 13, marginBottom: 20 },
  tabs: { display: "flex", gap: 8, marginBottom: 16 },
  tab: { padding: "8px 16px", background: "#21262d", border: "1px solid #30363d", borderRadius: 8, color: "#8b949e", cursor: "pointer", fontSize: 13 },
  tabActive: { background: "#1f6feb", borderColor: "#1f6feb", color: "#fff" },
  trainBox: { background: "#161b22", border: "1px solid #30363d", borderRadius: 10, padding: 20, marginBottom: 24 },
  input: { width: "100%", padding: "10px 14px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#fff", fontSize: 14, boxSizing: "border-box", marginBottom: 12 },
  textarea: { width: "100%", padding: "10px 14px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#fff", fontSize: 14, boxSizing: "border-box", marginBottom: 12, resize: "vertical" },
  fileBtn: { padding: "10px 16px", background: "#21262d", border: "1px solid #30363d", borderRadius: 8, color: "#e6edf3", cursor: "pointer", marginBottom: 12, fontSize: 13 },
  trainBtn: { padding: "10px 24px", background: "#238636", border: "none", borderRadius: 8, color: "#fff", fontWeight: "bold", cursor: "pointer", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 },
  trainBtnDisabled: { background: "#2ea04380", cursor: "not-allowed" },
  sourceRow: { display: "flex", justifyContent: "space-between", alignItems: "center", background: "#161b22", border: "1px solid #30363d", borderRadius: 8, padding: "10px 14px", marginBottom: 8 },
  sourceType: { background: "#1f6feb33", color: "#58a6ff", borderRadius: 4, padding: "2px 8px", fontSize: 11, marginRight: 10 },
  sourceName: { color: "#e6edf3", fontSize: 13 },
  sourceChunks: { color: "#8b949e", fontSize: 11, marginLeft: 10 },
  deleteBtn: { background: "transparent", border: "none", cursor: "pointer", fontSize: 16, color: "#f85149" },
  refreshBtn: { background: "#21262d", border: "1px solid #30363d", borderRadius: 8, color: "#8b949e", cursor: "pointer", fontSize: 16, padding: "4px 10px", display: "inline-flex", alignItems: "center" },
};
